import { AssetComponent } from '../../../../redux/slices/slice_types';
import AssetIcon from './AssetIcon';
import { Typography, Grid, Box } from '@mui/material';
import ClickCopy from '../../../_common/ClickCopy/ClickCopy';
import { insertWordBreaks, useHistory } from '@/_utils/utils';
import OriginJob from './OriginJob';
import { selectPendingAssetSystemStatuses } from '../../../../redux/selectors/constantsSelectors';
import { getAgeFromDate } from '../../../../_utils/date';
import { selectJobCreatedAt } from '../../../../redux/selectors/jobSelectors';
import PolarisIcon from '../../../_common/PolarisIcon';
import styled from '@emotion/styled';
import { TableStatus } from '../../../_common/Table';
import ViewJobAssetButton from './ViewJobAssetButton';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { applyFilter, clearFilters, setCurrentTable } from '@/redux/slices/dataTableSlice';

interface AssetComponentBoxProps {
  asset: AssetComponent;
}

const AssetComponentBox = ({ asset }: AssetComponentBoxProps) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const humanStatuses = useAppSelector(selectPendingAssetSystemStatuses);
  const createdAt = useAppSelector(selectJobCreatedAt);
  const age = createdAt && getAgeFromDate(createdAt);
  const assetStatus = asset.status === 'received' ? 'complete' : asset.status;

  const handleAssetClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (!asset.related_asset_id) return;

    dispatch(setCurrentTable('pendingAssets'));
    dispatch(clearFilters());
    if (typeof asset.related_asset_id === 'object') {
      const ids = asset.related_asset_id.map((id) => 'A' + id.toString());
      dispatch(applyFilter({ type: 'filter_id', selections: [ids, 'job_asset_hid_filter'] }));
    } else {
      dispatch(applyFilter({ type: 'filter_id', selections: ['A' + asset.related_asset_id.toString(), 'job_asset_hid_filter'] }));
    }
    history.push('/pending-assets');
  };

  return (
    <AssetWrapper status={assetStatus} linkable={!!asset.related_asset_id} onClick={asset.related_asset_id ? handleAssetClick : undefined}>
      <AssetIcon type={asset.type} />
      <Typography variant="subtitle2" component="div">
        {asset.requested_order && <span>{asset.requested_order}:</span>}
        <ClickCopy copyText={asset.filename}>{insertWordBreaks(asset.filename)}</ClickCopy>
        {asset.material_id && <IdCaption variant="caption">{asset.material_id}</IdCaption>}
        <div>
          <OriginJob assetOriginJob={asset.origin_job_id} />
        </div>
      </Typography>
      <StatusTypography>
        {assetStatus !== 'complete' ? (
          <Grid container direction="column" alignItems="flex-end">
            <Box sx={{ mr: asset.status_reason ? -1 : 0 }}>
              <TableStatus
                status={assetStatus}
                customStatuses={humanStatuses}
                statusReason={asset.status_reason || asset.related_asset_status_reason}
                statusReasonAppend={asset.status_reason && <ViewJobAssetButton asset={asset} />}
              />
            </Box>
            {assetStatus !== 'cancelled' && (
              <Age container alignItems="center" justifyContent="flex-end">
                <PolarisIcon icon="timer" />
                <Typography variant="caption">{asset.status_changed_at ? getAgeFromDate(asset.status_changed_at) : age}</Typography>
              </Age>
            )}
          </Grid>
        ) : (
          <Grid container sx={{ color: 'success.main' }}>
            <PolarisIcon icon="check" height={24} />
          </Grid>
        )}
      </StatusTypography>
    </AssetWrapper>
  );
};

export default AssetComponentBox;

const AssetWrapper = styled.div<{ status: string; linkable: boolean }>`
  background: ${({ theme }) => `linear-gradient(90deg, ${theme.palette.warning.main}11, ${theme.palette.warning.main}2a)`};
  padding: ${({ theme }) => theme.spacing(1.25, 2)};
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  border: ${({ theme }) => `1px solid ${theme.palette.warning.main}5a`};
  display: inline-flex;
  align-items: center;
  position: relative;
  width: 100%;
  min-height: 4rem;
  cursor: ${({ linkable }) => (linkable ? 'pointer' : 'default')};

  .MuiTypography-subtitle2 {
    opacity: 0.7;
    word-break: break-word;
    padding-right: ${({ theme }) => theme.spacing(2)};
    margin-right: auto;
    & .MuiButton-root {
      font-weight: ${({ theme }) => theme.typography.fontWeightBold};
    }
  }

  ${({ status, theme }) =>
    status === 'complete'
      ? `
    background: linear-gradient(90deg, ${theme.palette.success.main}11, ${theme.palette.success.main}2a);
    border-color: ${theme.palette.success.main}5a;

    .MuiTypography-subtitle2 {
      opacity: 1;
    }
  `
      : status.includes('failed')
        ? `
    background: linear-gradient(90deg, ${theme.palette.error.main}11, ${theme.palette.error.main}2a);
    border-color: ${theme.palette.error.main}5a;
    strong {
      color: ${theme.palette.error.main};
    }
  `
        : status.includes('cancelled')
          ? `
    background: linear-gradient(90deg, ${theme.palette.info.main}11, ${theme.palette.grey[400]}2a);
    border-color: ${theme.palette.grey[400]}5a;
  `
          : ''}
`;

const StatusTypography = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
  color: ${({ theme }) => theme.palette.warning.main};
  white-space: nowrap;
  display: flex;
  align-items: center;
  text-transform: uppercase;
  margin-left: auto;
  margin-right: 0;
  opacity: 1;
  overflow: visible;
  font-size: 0.9rem;
  & .MuiTypography-caption {
    font-size: 0.9rem;
    opacity: 0.7;
  }
`;

const Age = styled(Grid)`
  color: ${({ theme }) => theme.palette.text.primary};
  opacity: 0.66;
  font-size: 0.9rem;
  text-transform: none;
  flex-wrap: nowrap;

  svg {
    font-size: 0.82rem;
    margin-right: ${({ theme }) => theme.spacing(0.25)};
    font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  }
`;

const IdCaption = styled(Typography)`
  font-size: 0.75rem;
  font-weight: 800;
`;
